import React, { startTransition, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  Divider,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  Language,
  GppGood,
  Sensors
} from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { clientServiceV2 } from "src/clientService";
import { useSnackbar } from "src/view/components/SnackbarService";
import authBg from "src/resources/images/auth-bg.png";
import logoImgWhite from "src/resources/images/logo-white 1.png";
import { getPasswordPolicyError, getPasswordStrength, PASSWORD_POLICY_TEXT } from "src/util/password-policy";

const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { showSnackbar, snackbarType } = useSnackbar();

  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidLink, setIsValidLink] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const passwordError = newPassword ? getPasswordPolicyError(newPassword) : "";
  const passwordStrength = getPasswordStrength(newPassword);

  const inputStyles = {
    mt: 0.5,
    mb: 1.5,
    '& .MuiFilledInput-root': {
      bgcolor: '#0a1410',
      color: '#FFFFFF',
      borderRadius: '8px',
      border: '1px solid rgba(0, 242, 150, 0.1)',
      '&:before, &:after': { display: 'none' },
      '&:hover': { bgcolor: 'rgba(0, 242, 150, 0.05)' },
      '&.Mui-focused': { bgcolor: '#0a1410', borderColor: '#00f296' },
      '& input': { py: 1.25 },
    },
    '& .MuiFormHelperText-root': { color: '#FF5252' },
  };

  // Step 1: Verify token on mount
  useEffect(() => {
    let isMounted = true;

    const verifyLink = async () => {
      if (!token) {
        if (isMounted) {
          setIsVerifying(false);
          showSnackbar({ message: "Invalid reset link", type: snackbarType.ERROR });
        }
        return;
      }

      try {
        await clientServiceV2.api.verifyPasswordResetLinkApiV2VerifyPasswordLinkPost({ token });
        if (isMounted) {
          setIsValidLink(true);
        }
      } catch (err: unknown) {
        if (isMounted) {
          const error = err as any;
          const msg = error.response?.data?.detail || "Invalid or expired reset link.";
          showSnackbar({ message: msg, type: snackbarType.ERROR });
          setIsValidLink(false);
        }
      } finally {
        if (isMounted) {
          setIsVerifying(false);
        }
      }
    };

    verifyLink();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Step 2: Handle password reset
  const handleResetPassword = async () => {
    if (!newPassword) {
      showSnackbar({ message: "Please enter your new password.", type: snackbarType.WARNING });
      return;
    }
    if (passwordError) {
      showSnackbar({ message: PASSWORD_POLICY_TEXT, type: snackbarType.WARNING });
      return;
    }

    setIsSubmitting(true);
    try {
      await clientServiceV2.api.resetUserPasswordApiV2ResetPasswordPost({
        token: token as string,
        new_password: newPassword,
      });

      showSnackbar({ message: "Password reset successful! You can now log in." });
      startTransition(() => navigate('/'));
    } catch (err: unknown) {
      const error = err as any;
      const msg =
        error?.error?.message ||
        error?.error?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        (typeof error?.detail === "string" ? error.detail : null) ||
        (typeof error?.message === "string" ? error.message : null) ||
        "Failed to reset password.";
      setSubmitError(msg);
      showSnackbar({ message: msg, type: snackbarType.ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#040b08', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#00f296' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#040b08', overflow: 'hidden' }}>
      {/* Left Section - Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 4, md: 5 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <Box sx={{ mb: 4 }}>
            <img src={logoImgWhite} alt="Darukaa Earth" style={{ height: '32px' }} />
          </Box>

          {!isValidLink ? (
            <Box>
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 2 }}>
                Oops!
              </Typography>
              <Typography sx={{ color: '#9BA1A6', mb: 4 }}>
                Invalid or expired password reset link.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate("/")}
                sx={{
                  bgcolor: '#00f296',
                  color: '#040b08',
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#00d984' },
                }}
              >
                Go to Login
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                Reset Password
              </Typography>
              <Typography sx={{ color: '#9BA1A6', mb: 2 }}>
                Enter your new password below.
              </Typography>

              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 0.5 }}>
                New Password
              </Typography>
              <TextField
                fullWidth
                variant="filled"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (submitError) setSubmitError("");
                }}
                sx={inputStyles}
                disabled={isSubmitting}
                error={!!passwordError || !!submitError}
                helperText={submitError || passwordError || PASSWORD_POLICY_TEXT}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#9BA1A6' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {newPassword && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -1, mb: 2 }}>
                  <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                    {[1, 2, 3, 4].map(step => (
                      <Box
                        key={step}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: step <= passwordStrength.score ? '#00f296' : '#2D3139',
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: '#9BA1A6' }}>
                    {passwordStrength.label}
                  </Typography>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleResetPassword}
                disabled={isSubmitting}
                endIcon={!isSubmitting && <ArrowForward />}
                sx={{
                  mt: 1,
                  bgcolor: '#00f296',
                  color: '#040b08',
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#00d984' },
                  '&.Mui-disabled': { bgcolor: '#2D3139', color: '#555A60' }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/')}
                  sx={{ color: '#9BA1A6', textDecoration: 'none', '&:hover': { color: '#00f296' } }}
                >
                  Back to Login
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Section - Branding */}
      <Box
        sx={{
          flex: { xs: 0, md: 1.2 },
          display: { xs: 'none', md: 'flex' },
          backgroundImage: `linear-gradient(rgba(11, 14, 17, 0.4), rgba(11, 14, 17, 0.4)), url(${authBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
          p: 8,
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: 500 }}>
          <Typography
            variant="h2"
            sx={{
              color: '#FFFFFF',
              fontWeight: 800,
              lineHeight: 1.2,
              mb: 3,
            }}
          >
            Securing the Future of Our Planet.
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 400,
              mb: 6,
              lineHeight: 1.6,
            }}
          >
            Recover your access to the most comprehensive platform for Carbon and Biodiversity management.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Language sx={{ color: '#00f296', fontSize: 32 }} />
              <Box>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>Global Reach</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Managing projects across 20+ countries.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <GppGood sx={{ color: '#00f296', fontSize: 32 }} />
              <Box>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>Verified Data</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  High-integrity carbon & biodiversity credits.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Sensors sx={{ color: '#00f296', fontSize: 32 }} />
              <Box>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>Real-time Analysis</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Satellite and IoT powered insights.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ position: 'absolute', bottom: 40, left: 96, right: 96 }}>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
              © {new Date().getFullYear()} Darukaa Earth. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', cursor: 'pointer' }}>Privacy Policy</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', cursor: 'pointer' }}>Terms of Service</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PasswordResetPage;
